import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY, forkJoin, Observable, of, throwError, catchError } from 'rxjs';
import { concatMap, concatWith, delay, map, mergeMap, retry, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LocationService } from 'app/shared/services/location.service';
import { LocationCardService } from 'app/shared/components/location-card/services/location-card.service';
import { DataEditService } from 'app/shared/services/data-edit.service';
import {
    CoreActionType,
    flexSetCreateMode,
    FormActionType,
    initForm,
    initState,
    loadAvailableDynamicSections,
    loadAvailableDynamicSectionsSuccess,
    loadCustomer,
    loadCustomerSuccess,
    loadDynamicSectionsSuccess,
    loadExistingDynamicSectionsSuccess,
    loadInstallationTypes,
    loadInstallationTypesFailure,
    loadInstallationTypesSuccess,
    loadLatestSiltValue,
    loadLocationDetailsSuccess,
    loadLocations,
    loadMonitorConfigurationSuccess,
    loadMonitorSeries,
    loadQFinalEntities,
    loadSensorCollection,
    loadSensorCollectionSuccess,
    loadSensorCollectionFailure,
    saveSensorCollection,
    saveSensorCollectionSuccess,
    saveSensorCollectionFailure,
    populateForm,
    preloadDynamicSectionsSuccess,
    setEchoSiltValue,
    setFlexSiltValue,
    setFormData,
    setFormSettings,
    setPermissions,
    setFieldValue,
    setTritonSiltValue,
    activateLocation,
    submitForm,
    transitionToEditMode,
    transitionToEditModeSuccess,
    loadDiagnosticsSuccess,
} from '../actions';
import { MapService } from 'app/shared/services/map.service';
import { Action, Store } from '@ngrx/store';
import { FlexCreateMode, LocationCardPermissions, LocationCardSettings, LocationCardState, MonitorConfiguration } from '../reducers';
import { gatherTritonBasicInfoPayload, gatherTritonConfigurationInfo } from '../configs/triton/base-api-data';
import { LocationPayload, TritonLocationService } from 'app/shared/services/tritonLocation.service';
import { ActivateTritonResponse } from 'app/shared/models/TritonLocation';
import { LocationStatus } from 'app/shared/models/locations';
import { BASE_MONITOR_SERIES } from '../constants/base';
import { CustomerService } from 'app/shared/services/customer.service';
import { LocationCardData } from '../../models/location-card.model';
import { LocationDetails, MonitorSeriesUI } from 'app/shared/models/location-details';
import { Customer } from 'app/shared/models/customer';
import { HTTP_STATUS_CODE_CONCURRENCY, HTTP_STATUS_CODE_CONFLICT, SILT_ENTITY, UnitOfMeasureType } from 'app/shared/constant';
import { TranslateService } from '@ngx-translate/core';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { MonitorSeriesNames } from '../../location-card.constants';
import { MonitorSettingService } from 'app/shared/services/monitor-setting.service';
import { findFieldById } from '../configs/base-utils';
import { BasicInfoCommonFieldIds } from '../configs/common.models';
import { gatherEchoBasicInfoPayload, gatherEchoConfigurationInfo } from '../configs/echo/base-api-data';
import { gatherRainBasicInfoPayload, gatherRainConfigurationInfo } from '../configs/rain-alert/base-api-data';
import {
    gatherForeSiteULBasicInfoPayload,
    gatherForeSiteULconfigurationInfo,
} from '../configs/foresite-ul/base-api-data';
import {
    gatherForeSiteXDBasicInfoPayload,
    gatherForeSiteXDconfigurationInfo,
} from '../configs/foresite-xd/base-api-data';
import {
    gatherFlexBasicInfoPayload,
    gatherFlexConfigurationInfo,
    gatherFlexDynamicSectionPayload,
} from '../configs/flex/base-api-data';
import { gatherGenericBasicInfoPayload } from '../configs/generic/base-api-data';
import { gatherEdgeDxBasicInfoPayload } from '../configs/edge-dx/base-api-data';
import { EdgeDxLocationPayload } from '../configs/edge-dx/base-api-models';
import { gatherEdgeRlBasicInfoPayload, gatherEdgeRlConfigurationInfo } from '../configs/edge-rl/base-api-data';
import { gatherUnknownBasicInfoPayload } from '../configs/unknown/base-api-data';
import { UnknownLocationPayload } from '../configs/unknown/base-api-models';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { FieldType, SelectField } from '../models/field-models';
import { LocationCardMonitorSeries, MonitorIdentifier } from 'app/shared/models/monitorsetting';
import { UsersService } from 'app/pages/admin/users.service';
import { StatusCodeService } from 'app/shared/services/status-code.service';
import { MonitorDiagnosticsService } from 'app/shared/services/monitor-diagnostics.service';
import { MONITOR_SERIES_TYPES } from 'app/shared/models/monitor-series-types';
import { AvailableDynamicSection } from '../models/dynamic-section-models';
import { HttpErrorResponse } from '@angular/common/http';
import { EchoConfigurationPayload, EchoLocationPayload } from '../configs/echo/base-api-models';
import {
    TritonConfigurationPayload,
    TritonLocationBaseInfo,
    TritonLocationPayload,
} from '../configs/triton/base-api-models';
import { ForeSiteULConfigurationPayload, ForeSiteULLocationPayload } from '../configs/foresite-ul/base-api-models';
import { RainAlertConfigurationPayload, RainAlertLocationPayload } from '../configs/rain-alert/base-api-models';
import { ForeSiteXDConfigurationPayload } from '../configs/foresite-xd/base-api-models';
import { FlexLocationPayload } from '../configs/flex/base-api-models';
import { GenericLocationPayload } from '../configs/generic/base-api-models';
import { EdgeRlConfigurationPayload, EdgeRlLocationPayload } from '../configs/edge-rl/base-api-models';
import { EchoLocationCardState } from '../configs/echo/models';
import { TritonLocationCardState } from '../configs/triton/models';
import { RainAlertLocationCardState } from '../configs/rain-alert/models';
import { ForesiteULLocationCardState } from '../configs/foresite-ul/models';
import { ForesiteXDLocationCardState } from '../configs/foresite-xd/models';
import { FlexLocationCardState, FlexMonitorConfiguration } from '../configs/flex/models';
import { GenericLocationCardState } from '../configs/generic/models';
import { EdgeRlLocationCardState } from '../configs/edge-rl/models';
import { UnknownLocationCardState } from '../configs/unknown/models';
import { Config } from 'app/shared/services/config';
import { GainEntityData } from 'app/shared/models/gain-data';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
    ConfirmationButton,
    ConfirmationDialogComponent,
} from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { IComponentDialog, IComponentDialogConfirmationResult } from 'app/shared/models/component-confirmation';

@Injectable()
export class LocationCardEffects {
    private actions$: Actions<Action> = inject(Actions);
    private locationService = inject(LocationService);
    public locationCardService = inject(LocationCardService);
    private mapService = inject(MapService);
    private translateService = inject(TranslateService);
    private dateUtilService = inject(DateutilService);
    private store: Store<{ locationCard: LocationCardState<unknown> }> = inject(Store);

    private tritonLocationService = inject(TritonLocationService);
    private monitorSettingService = inject(MonitorSettingService);
    private customerService = inject(CustomerService);
    private dialog = inject(MatDialog);
    private snackBar = inject(MatSnackBar);
    private dataEditService = inject(DataEditService);

    private usersService = inject(UsersService);
    private statusCodeService = inject(StatusCodeService);
    private monitorDiagnosticsService = inject(MonitorDiagnosticsService);
    private timezoneText = '';
    constructor() {}

    loadDiagnostics$ = createEffect(() =>
        this.actions$.pipe(
            ofType(populateForm),
            withLatestFrom(this.store),
            switchMap(([_, storeState]) => {
                const state = storeState.locationCard;

                if (!state.permissions.isMonitorEditor || !state.locationDetails?.length) {
                    return of(loadDiagnosticsSuccess({ showDiagnostics: false, deviceTypes: [] }));
                }

                const details = state.locationDetails[0];
                return this.monitorDiagnosticsService
                    .getDiagnosticsDetails(
                        details.series as MONITOR_SERIES_TYPES,
                        details.locationID,
                        state.locationCardData.customerId,
                    )
                    .pipe(
                        map((result) => loadDiagnosticsSuccess(result)),
                        catchError(() => of(loadDiagnosticsSuccess({ showDiagnostics: false, deviceTypes: [] }))),
                    );
            }),
        ),
    );

    initState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initState),
            switchMap(({ data }) => {
                const customerId = data.customerId;
                const isEditMode = data.editMode && data.locationDetails;
                const requests: Observable<
                    LocationCardMonitorSeries[] | LocationDetails[] | Customer | MonitorConfiguration
                >[] = [this.customerService.getCustomerById(customerId)];

                const seriesRequest = this.locationService.getMonitorSeries(true).pipe(
                    map((series) => {
                        // #46083 Temporarily disabling FLEX monitor series from Test and Prod environments
                        const formattedSeries = series
                            .filter((v) =>
                                Config.isTestEnv || Config.isProdEnv ? (v.series !== MonitorSeriesNames.flex) : true,
                            )
                            .map((monitor) => {
                                const baseEntry = BASE_MONITOR_SERIES.find(
                                    (b) => b.value.toLowerCase() === monitor.modelName.toLowerCase(),
                                );
                                return {
                                    // Use TritonPlus monitorSeriesID for FLEX, Edge-RL and Generic Monitor to fetch correct installation types
                                    monitorSeriesID: (monitor.series === MonitorSeriesNames.flex || monitor.series === MonitorSeriesNames.edgeRl || monitor.series === MonitorSeriesNames.genericMonitor)
                                        ? MonitorIdentifier.TritonPlus
                                        : monitor.monitorSeriesID,
                                    value: baseEntry ? baseEntry.value : monitor.modelName.toUpperCase(),
                                    text: monitor.modelName + '',
                                    batteryLowInt: monitor.batteryLowInt,
                                };
                            });

                        if (data.locationDetails && data.locationDetails.series === MonitorSeriesNames.usgs) {
                            formattedSeries.push({
                                monitorSeriesID: 0,
                                value: MonitorSeriesNames.usgs,
                                text: MonitorSeriesNames.usgs,
                                batteryLowInt: 0,
                            });
                        }

                        return formattedSeries;
                    }),
                );

                requests.push(seriesRequest);

                if (isEditMode && data.locationDetails) {
                    requests.push(
                        this.locationService.getLocationDetailsV2(data.customerId, data.locationDetails.locationID),
                    );

                    requests.push(
                        this.getConfigurationRequest(
                            data.locationDetails.series as MonitorSeriesNames,
                            data.customerId,
                            data.locationDetails.locationID,
                        ).pipe(
                            catchError((error) => {
                                this.snackBar.open(
                                    this.translateService.instant('COMMON.LOCATION_FETCHED_ERROR'),
                                    this.translateService.instant('COMMON.DISMISS_TEXT'),
                                    {
                                        panelClass: 'custom-error-snack-bar',
                                        duration: 5000,
                                    },
                                );
                                this.closeDialog(false);
                                return of(null);
                            }),
                        ),
                    );
                }

                return combineLatest(requests).pipe(
                    concatMap(
                        ([customer, series, locationDetails, monitorConfig]: [
                            Customer,
                            LocationCardMonitorSeries[],
                            LocationDetails[],
                            MonitorConfiguration,
                        ]) => {
                            this.loadTimezone(customer);

                            const actions: Array<Action> = [
                                loadCustomerSuccess({ customer }),
                                setFormSettings({ settings: this.gatherSettings(customer) }),
                                setPermissions({ permissions: this.gatherPermissions() }),
                            ];

                            if (series && series.length) {
                                actions.push(loadMonitorSeries({ series }));
                            }

                            if (isEditMode && locationDetails && locationDetails.length && series) {
                                if (locationDetails[0].externalVendorID === 1) {
                                    locationDetails[0].series = MonitorSeriesNames.usgs;
                                    if (!series.some(s => s.value === MonitorSeriesNames.usgs)) {
                                        series.push({
                                            monitorSeriesID: 0,
                                            value: MonitorSeriesNames.usgs,
                                            text: MonitorSeriesNames.usgs,
                                            batteryLowInt: 0,
                                        });
                                    }
                                }

                                if(locationDetails[0].maintenanceEnd)
                                {
                                    locationDetails[0].maintenanceEnd = this.locationCardService.convertDate(locationDetails[0].maintenanceEnd);
                                }
                                actions.push(
                                    populateForm({ details: locationDetails, config: monitorConfig, series }),
                                    loadLatestSiltValue(),
                                );
                            } else if (isEditMode && (!locationDetails || !locationDetails.length)) {
                                this.snackBar.open(
                                    this.translateService.instant('COMMON.LOCATION_FETCHED_ERROR'),
                                    this.translateService.instant('COMMON.DISMISS_TEXT'),
                                );
                                this.closeDialog(false);
                                return actions;
                            } else {
                                actions.push(initForm());
                            }

                            return actions;
                        },
                    ),
                );
            }),
        ),
    );

    populateForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(populateForm),
            switchMap(({ details, config }) => {
                const installationId = BASE_MONITOR_SERIES.find(
                    (v) => v.value.toLowerCase() === details[0].series.toLowerCase(),
                )?.monitorSeriesID;

                if (!installationId) {
                    return of(loadInstallationTypesSuccess({ data: [] })).pipe(
                        concatWith(of(loadAvailableDynamicSections())),
                    );
                }

                return this.locationService.getInstallationType(installationId).pipe(
                    switchMap((installationTypes) =>
                        of(loadInstallationTypesSuccess({ data: installationTypes })).pipe(
                            concatWith(of(loadAvailableDynamicSections())),
                        ),
                    ),
                    catchError((error) =>
                        of(loadInstallationTypesFailure({ error })).pipe(
                            concatWith(of(loadAvailableDynamicSections())),
                        ),
                    ),
                );
            }),
        ),
    );

    submitForm$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(submitForm),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    const locationCardState = state.locationCard;
                    if (!locationCardState.permissions.isMonitorEditor) {
                        return of(null);
                    }

                    const { shouldClose, isActivationRequired } = action;
                    const customer = locationCardState.customer;
                    const seriesField = findFieldById(
                        locationCardState.formState,
                        BasicInfoCommonFieldIds.Series,
                        FieldType.Select,
                    ) as SelectField<LocationCardMonitorSeries, string>;
                    const series = seriesField.value;

                    switch (series.toLowerCase()) {
                        case MonitorSeriesNames.triton.toLowerCase():
                            this.submitTritonForm(customer, locationCardState as TritonLocationCardState, shouldClose, isActivationRequired);
                            break;
                        case MonitorSeriesNames.echo.toLowerCase():
                            this.submitEchoForm(customer, locationCardState as EchoLocationCardState, shouldClose, isActivationRequired);
                            break;
                        case MonitorSeriesNames.rainAlert.toLowerCase():
                            this.submitRainAlertForm(
                                customer,
                                locationCardState as RainAlertLocationCardState,
                                shouldClose,
                                isActivationRequired,
                            );
                            break;
                        case MonitorSeriesNames.ForeSITE_UL.toLowerCase():
                            this.submitForeSiteULForm(
                                customer,
                                locationCardState as ForesiteULLocationCardState,
                                shouldClose,
                            );
                            break;
                        case MonitorSeriesNames.ForeSITE_XD.toLowerCase():
                            this.submitForeSiteXDForm(
                                customer,
                                locationCardState as ForesiteXDLocationCardState,
                                shouldClose,
                            );
                            break;
                        case MonitorSeriesNames.flex.toLowerCase():
                            this.submitFlexForm(customer, locationCardState as FlexLocationCardState, shouldClose);
                            break;
                        case MonitorSeriesNames.genericMonitor.toLowerCase():
                            this.submitGenericForm(
                                customer,
                                locationCardState as GenericLocationCardState,
                                shouldClose,
                            );
                            break;
                        case MonitorSeriesNames.edgeDx.toLowerCase():
                            this.submitEdgeDxForm(customer, locationCardState, shouldClose);
                            break;
                        case MonitorSeriesNames.edgeRl.toLowerCase():
                            this.submitEdgeRlForm(
                                customer,
                                locationCardState as EdgeRlLocationCardState,
                                shouldClose,
                            );
                            break;
                        default:
                            this.submitOtherMonitorForm(customer, locationCardState, shouldClose);
                            break;
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    activateLocation$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(activateLocation),
                withLatestFrom(this.store),
                switchMap(([_, state]) => {
                    const locationCardState = state.locationCard;
                    if (!locationCardState.permissions.isMonitorEditor) {
                        return of(null);
                    }

                    const customer = locationCardState.customer;
                    const [mp1] = locationCardState.locationDetails;

                    this.snackBar.open(
                        this.translateService.instant('COMMON.MONITOR_ACTIVATE_REQUEST_INITIATED'),
                        this.translateService.instant('COMMON.DISMISS_TEXT'),
                        { duration: 3000 },
                    );

                    const lon = findFieldById(locationCardState.formState, BasicInfoCommonFieldIds.Longitude)?.value as number;
                    const lat = findFieldById(locationCardState.formState, BasicInfoCommonFieldIds.Latitude)?.value as number;

                    if (!lat) {
                        this.store.dispatch(setFieldValue({ fieldId: BasicInfoCommonFieldIds.Latitude, value: 1 }));
                    }
                    if (!lon) {
                        this.store.dispatch(setFieldValue({ fieldId: BasicInfoCommonFieldIds.Longitude, value: 1 }));
                    }

                    if (!locationCardState.pristine) {
                        this.store.dispatch(submitForm({ shouldClose: true, isActivationRequired: true }));
                    } else {
                        const locationNameField = findFieldById(locationCardState.formState, BasicInfoCommonFieldIds.LocationName, FieldType.Text);
                        const locationName = locationNameField?.value ?? '';
                        const seriesField = findFieldById(
                            locationCardState.formState,
                            BasicInfoCommonFieldIds.Series,
                            FieldType.Select,
                        ) as SelectField<LocationCardMonitorSeries, string>;
                        const series = seriesField.value.toUpperCase();
                        const isInactive = findFieldById(locationCardState.formState, BasicInfoCommonFieldIds.Status)?.value === LocationStatus.Inactive;
                        const isForeSite = series === MonitorSeriesNames.ForeSITE_UL || series === MonitorSeriesNames.ForeSITE_XD;

                        if (!isInactive && !isForeSite) {
                            this.activateMonitor(customer.customerID, mp1.locationID, locationName, true);
                        } else {
                            this.closeDialog(true);
                        }
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    laodCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCustomer),
            switchMap(({ customerId }) =>
                this.customerService.getCustomerById(customerId).pipe(
                    map((customer) => {
                        return loadCustomerSuccess({ customer });
                    }),
                ),
            ),
        ),
    );

    loadAvailableDynamicSections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadAvailableDynamicSections),
            switchMap(() => this.locationService.getAvailableDynamicSensors()),
            map((sensors) => loadAvailableDynamicSectionsSuccess({ data: sensors })),
        ),
    );

    preloadDynamicSections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadAvailableDynamicSectionsSuccess),
            withLatestFrom(this.store),
            switchMap(([{ data }, state]) => {
                if (!data || data.length === 0) {
                    return of(preloadDynamicSectionsSuccess({ configTemplates: [] }));
                }

                const customerId = state.locationCard.locationCardData.customerId;
                const configRequests = data.map((sensor) =>
                    this.locationService
                        .getFlexDynamicSensorConfig(sensor.name, sensor.version, sensor.sensorType, customerId)
                        .pipe(
                            catchError((error) => {
                                console.error(`Error loading config for sensor ${sensor.name}:`, error);
                                return of(null);
                            }),
                        ),
                );

                return forkJoin(configRequests).pipe(
                    map((results) => results.filter(Boolean)),
                    map((configTemplates) => preloadDynamicSectionsSuccess({ configTemplates })),
                );
            }),
            catchError((error) => {
                console.error('Error preloading dynamic sections:', error);
                return of(preloadDynamicSectionsSuccess({ configTemplates: [] }));
            }),
        ),
    );

    checkExistingDynamicSections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(preloadDynamicSectionsSuccess),
            withLatestFrom(this.store),
            switchMap(([action, state]) => {
                const { locationCard } = state;
                const details =
                    locationCard.locationDetails && locationCard.locationDetails.length
                        ? locationCard.locationDetails[0]
                        : null;

                if (!details) {
                    return of(loadExistingDynamicSectionsSuccess({ data: [] }));
                }

                const deviceId = details.serialNumber;
                const series = details.series;

                if (!deviceId || !series || series.toLowerCase() !== MonitorSeriesNames.flex.toLowerCase()) {
                    return of(loadExistingDynamicSectionsSuccess({ data: [] }));
                }

                return this.locationService.getSensorConfigurationCollection(deviceId, locationCard.locationCardData.customerId).pipe(
                    switchMap((collection) => {
                        if (!collection.sensors || collection.sensors.length === 0) {
                            return [
                                loadSensorCollectionSuccess({ collection }),
                                loadExistingDynamicSectionsSuccess({ data: [] })
                            ];
                        }

                        return [
                            loadSensorCollectionSuccess({ collection }),
                            loadExistingDynamicSectionsSuccess({ data: [collection.sensors[0]] })
                        ];
                    }),
                    catchError((error) => {
                        console.warn('Error loading sensor configuration collection:', error);

                        return of(loadExistingDynamicSectionsSuccess({ data: [] }));
                    }),
                );
            }),
        ),
    );

    loadSensorCollection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSensorCollection),
            switchMap(({ deviceId, customerId }) =>
                this.locationService.getSensorConfigurationCollection(deviceId, customerId).pipe(
                    map((collection) => loadSensorCollectionSuccess({ collection })),
                    catchError((error: HttpErrorResponse) =>
                        of(loadSensorCollectionFailure({ error }))
                    )
                )
            )
        )
    );

    saveSensorCollection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveSensorCollection),
            withLatestFrom(this.store),
            switchMap(([{ deviceId, customerId, collection }, state]) => {
                if (!state.locationCard.permissions.isMonitorEditor) {
                    return EMPTY;
                }

                return this.locationService.saveSensorConfigurationCollection(deviceId, customerId, collection).pipe(
                    map(() => saveSensorCollectionSuccess()),
                    catchError((error: HttpErrorResponse) =>
                        of(saveSensorCollectionFailure({ error }))
                    )
                );
            }),
        ),
    );

    loadLatestSiltValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadLatestSiltValue),
            withLatestFrom(this.store),
            switchMap(([action, state]) => {
                const { locationCard } = state;
                const series = locationCard?.locationDetails[0]?.series;

                return combineLatest(
                    locationCard.locationDetails.map((locDetails) =>
                        this.dataEditService.getAllEntityData(
                            locationCard.locationCardData.customerId,
                            locDetails.locationID,
                            SILT_ENTITY,
                        ),
                    ),
                ).pipe(
                    mergeMap((siltDataArray: GainEntityData[]) => {
                        const actions: Action[] = [];

                        siltDataArray.forEach((entityData, index) => {
                            if (entityData && (entityData.data?.length > 0 || entityData.editedData?.length > 0)) {
                                const latestValue = this.extractLatestSiltValue(entityData);

                                switch (series) {
                                    case MonitorSeriesNames.triton:
                                        actions.push(setTritonSiltValue({ mp: index, value: latestValue }));
                                        break;
                                    case MonitorSeriesNames.echo:
                                        actions.push(setEchoSiltValue({ value: latestValue }));
                                        break;
                                    case 'FLEX':
                                        actions.push(setFlexSiltValue({ mp: index, value: latestValue }));
                                        break;
                                }
                            }
                        });

                        return actions;
                    }),
                );
            }),
        ),
    );

    public gatherSettings(customer: Customer): LocationCardSettings {
        const isMetric = customer.unitsType && Number(customer.unitsType) === UnitOfMeasureType.METRIC;

        const celciusTemp = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.CELSIUS_TEMP_UNITS');
        const fahrenheitTemp = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.FAHRENHEIT_TEMP_UNITS');
        const metricUnits = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.METRIC_UNITS');
        const usUnits = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.US_UNITS');
        const elevationMetricUnits = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.METER_UNITS');
        const elevationStandardUnits = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.FEET_UNITS');
        const dateFormat = this.dateUtilService.getStringFormat(customer.dateFormat);
        const voltUnit = this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.VOLT_UNIT');

        return {
            isMetric,
            tempUnit: isMetric ? celciusTemp : fahrenheitTemp,
            depthUnit: isMetric ? metricUnits : usUnits,
            elevationUnit: isMetric ? elevationMetricUnits : elevationStandardUnits,
            flowUnit: this.dateUtilService.peakVolumeUnit.getValue(),
            voltUnit,
            dateFormat,
        };
    }

    private gatherPermissions(): LocationCardPermissions {
        const userRoles = this.statusCodeService.userInfo.getValue()?.userRole;
        const isAdmin = Array.isArray(userRoles) && userRoles.length > 0 && userRoles[0] !== 'None';

        return {
            isMonitorEditor: this.usersService.isMonitorEditor.getValue(),
            isBasicDataEditingAllowed: this.usersService.isBasicDataEditingAllowed.getValue(),
            isAdmin,
        };
    }

    private getConfigurationRequest(
        series: MonitorSeriesNames,
        customerId: number,
        locationId: number,
    ): Observable<MonitorConfiguration | null> {
        switch (series.toLowerCase()) {
            case MonitorSeriesNames.triton.toLowerCase():
                return this.tritonLocationService.getTritonMonitorConfiguration(locationId, customerId);
            case MonitorSeriesNames.echo.toLowerCase():
                return this.monitorSettingService.getEchoMonitorConfiguration(customerId, locationId);
            case MonitorSeriesNames.rainAlert.toLowerCase():
                return this.monitorSettingService.getRainAlertMonitorConfiguration(customerId, locationId);
            case MonitorSeriesNames.ForeSITE_UL.toLowerCase():
                return this.monitorSettingService.foreSiteUlConfiguration(customerId, locationId);
            case MonitorSeriesNames.ForeSITE_XD.toLowerCase():
                return this.monitorSettingService.getForeSITEXDConfiguration(customerId, locationId);
            case MonitorSeriesNames.flex.toLowerCase():
                return this.monitorSettingService.getFlexConfiguration(customerId, locationId);
            case MonitorSeriesNames.edgeRl.toLowerCase():
                return this.monitorSettingService.getEdgeRlConfiguration(customerId, locationId);
            default:
                return of(null);
        }
    }

    transitionToEditMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(transitionToEditMode),
            switchMap(({ locationId, customerId }) => {
                const parallelRequests = [
                    this.locationService.getLocationDetailsV2(customerId, locationId),
                    this.monitorSettingService.getFlexConfiguration(customerId, locationId),
                    this.locationService.getMonitorSeries(true),
                    this.customerService.getCustomerById(customerId),
                ];

                return forkJoin(parallelRequests).pipe(
                    switchMap(
                        ([locationDetails, flexConfig, series, customer]: [
                            LocationDetails[],
                            FlexMonitorConfiguration,
                            MonitorSeriesUI[],
                            Customer,
                        ]) => {
                            const editModeData: LocationCardData = {
                                customerId,
                                editMode: true,
                                locationDetails: { locationID: locationId, series: 'FLEX' } as LocationDetails,
                                isFromCustomerEditor: false,
                                rainGaugeLocations: [],
                            };

                            const formattedSeries = series
                                .filter((v) =>
                                    // #46083 Temporarily disabling FLEX monitor on Test and Prod environments
                                    Config.isTestEnv || Config.isProdEnv ? (v.series !== MonitorSeriesNames.flex) : true,
                                )
                                .map((monitor) => {
                                    const baseEntry = BASE_MONITOR_SERIES.find(
                                        (b) => b.value.toLowerCase() === monitor.modelName.toLowerCase(),
                                    );
                                    return {
                                        monitorSeriesID: (monitor.series === MonitorSeriesNames.flex || monitor.series === MonitorSeriesNames.edgeRl || monitor.series === MonitorSeriesNames.genericMonitor)
                                            ? MonitorIdentifier.TritonPlus
                                            : monitor.monitorSeriesID,
                                        value: baseEntry ? baseEntry.value : monitor.modelName.toUpperCase(),
                                        text: monitor.modelName + '',
                                        batteryLowInt: monitor.batteryLowInt,
                                    };
                                });

                            const actions = [
                                flexSetCreateMode({ mode: FlexCreateMode.success }),
                                setFormData({ data: editModeData }),
                                loadCustomerSuccess({ customer }),
                                loadMonitorSeries({ series: formattedSeries }),
                                loadLocationDetailsSuccess({ details: locationDetails }),
                                loadMonitorConfigurationSuccess({ config: flexConfig }),
                                setFormSettings({ settings: this.gatherSettings(customer) }),
                                setPermissions({ permissions: this.gatherPermissions() }),
                                loadLocations({ customerId }),
                                loadQFinalEntities({ customerId, locationId }),
                                loadInstallationTypes({
                                    installationId:
                                        BASE_MONITOR_SERIES.find(
                                            (v) => v.value.toLowerCase() === MonitorSeriesNames.flex.toLowerCase(),
                                        )?.monitorSeriesID || 0,
                                }),
                                loadAvailableDynamicSections(),
                                populateForm({ details: locationDetails, config: flexConfig, series: formattedSeries }),
                                loadLatestSiltValue(),
                                transitionToEditModeSuccess(),
                            ];

                            return actions;
                        },
                    ),
                    catchError((error) => {
                        this.snackBar.open(
                            this.translateService.instant('COMMON.LOCATION_FETCHED_ERROR'),
                            this.translateService.instant('COMMON.DISMISS_TEXT'),
                            {
                                panelClass: 'custom-error-snack-bar',
                                duration: 5000,
                            },
                        );
                        return of(flexSetCreateMode({ mode: FlexCreateMode.error }));
                    }),
                );
            }),
        ),
    );

    // SUBMIT HANDLERS
    private submitEchoForm(customer: Customer, state: EchoLocationCardState, shouldClose: boolean, isActivationRequired: boolean) {
        const basicInfoPayload: EchoLocationPayload[] = gatherEchoBasicInfoPayload(state);
        const echoConfiguration: EchoConfigurationPayload = gatherEchoConfigurationInfo(state);
        echoConfiguration.timezone = this.timezoneText;

        const [mp1] = state.locationDetails;

        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
            echoConfiguration.locationid = mp1.locationID;
        }

        const isInactive = findFieldById(state.formState, BasicInfoCommonFieldIds.Status)?.value === LocationStatus.Inactive;
        const locationName: string = basicInfoPayload[0]['locationName'];

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    this.monitorSettingService
                        .echoMonitorConfiguration(echoConfiguration, customer.customerID)
                        .subscribe(() => {
                            this.showUpdateSuccessMessage(locationName);
                            this.handlePostConfigActivation(isActivationRequired, isInactive, customer.customerID, mp1.locationID, locationName, shouldClose);
                        });
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService
                .addLocation(customer.customerID, basicInfoPayload)
                .subscribe({
                    next: (locationId: number) => {
                        echoConfiguration.locationid = locationId;
                        this.monitorSettingService
                            .echoMonitorConfiguration(echoConfiguration, customer.customerID)
                            .subscribe(() => {
                                this.showSaveSuccessMessage(locationName);
                                if (isActivationRequired && !isInactive) {
                                    this.activateMonitor(customer.customerID, locationId, locationName, shouldClose);
                                } else if (shouldClose) {
                                    this.closeDialog(true);
                                } else {
                                    this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.echo);
                                }
                            });
                    },
                    error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
                });
        }
    }

    private submitTritonForm(customer: Customer, state: TritonLocationCardState, shouldClose: boolean, isActivationRequired: boolean) {
        const basicInfoPayload: TritonLocationPayload[] = gatherTritonBasicInfoPayload(state);
        const tritonConfiguration: TritonConfigurationPayload = gatherTritonConfigurationInfo(state);
        tritonConfiguration.timezone = this.timezoneText;

        const [mp1, mp2] = state.locationDetails;

        if (mp1 && basicInfoPayload[0]) {
            basicInfoPayload[0].locationid = mp1.locationID;
            tritonConfiguration.locationid = mp1.locationID;
        }

        if (mp2 && basicInfoPayload[1]) {
            basicInfoPayload[1].locationid = mp2.locationID;
        } else if (mp1 && basicInfoPayload[1]) {
            basicInfoPayload[1].locationid = 0; // mp2 was just added
        }

        const isInactive = findFieldById(state.formState, BasicInfoCommonFieldIds.Status)?.value === LocationStatus.Inactive;
        const locationName: string = basicInfoPayload[0]['locationName'];

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    if(!isInactive)
                    {
                        this.tritonLocationService
                            .tritonMonitorConfiguration(tritonConfiguration, customer.customerID)
                            .subscribe(() => {
                                this.showUpdateSuccessMessage(locationName);
                                this.handlePostConfigActivation(isActivationRequired, isInactive, customer.customerID, mp1.locationID, locationName, shouldClose);
                            });
                    }
                    else
                    {
                        if (shouldClose) {
                            this.closeDialog(true);
                        }
                        this.showUpdateSuccessMessage(locationName);
                    }
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService
                .addLocation(customer.customerID, basicInfoPayload)
                .subscribe({
                    next: (locationId: number) => {
                        tritonConfiguration.locationid = locationId;
                        if(!isInactive)
                        {
                            this.tritonLocationService
                            .tritonMonitorConfiguration(tritonConfiguration, customer.customerID)
                            .subscribe(() => {
                                this.showSaveSuccessMessage(locationName);
                                if (isActivationRequired && !isInactive) {
                                    this.activateMonitor(customer.customerID, locationId, locationName, shouldClose);
                                } else if (shouldClose) {
                                    this.closeDialog(true);
                                } else {
                                    this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.triton);
                                }
                            });
                        }
                    },
                    error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
                });
        }
    }

    private submitRainAlertForm(customer: Customer, state: RainAlertLocationCardState, shouldClose: boolean, isActivationRequired: boolean) {
        const basicInfoPayload: RainAlertLocationPayload[] = gatherRainBasicInfoPayload(state);
        const config: RainAlertConfigurationPayload = gatherRainConfigurationInfo(state);
        config.timezone = this.timezoneText;

        const [mp1] = state.locationDetails;
        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
            config.locationid = mp1.locationID;
        }

        const isInactive = findFieldById(state.formState, BasicInfoCommonFieldIds.Status)?.value === LocationStatus.Inactive;
        const locationName: string = basicInfoPayload[0]['locationName'];

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    this.monitorSettingService.rainAlert(config, customer.customerID).subscribe(() => {
                        this.showUpdateSuccessMessage(locationName);
                        this.handlePostConfigActivation(isActivationRequired, isInactive, customer.customerID, mp1.locationID, locationName, shouldClose);
                    });
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService
                .addLocation(customer.customerID, basicInfoPayload)
                .subscribe({
                    next: (locationId: number) => {
                        config.locationid = locationId;
                        this.monitorSettingService.rainAlert(config, customer.customerID).subscribe(() => {
                            this.showSaveSuccessMessage(locationName);
                            if (isActivationRequired && !isInactive) {
                                this.activateMonitor(customer.customerID, locationId, locationName, shouldClose);
                            } else if (shouldClose) {
                                this.closeDialog(true);
                            } else {
                                this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.rainAlert);
                            }
                        });
                    },
                    error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
                });
        }
    }

    private submitForeSiteULForm(customer: Customer, state: ForesiteULLocationCardState, shouldClose: boolean) {
        const basicInfoPayload: ForeSiteULLocationPayload[] = gatherForeSiteULBasicInfoPayload(state);
        const config: ForeSiteULConfigurationPayload = gatherForeSiteULconfigurationInfo(state);
        config.timezone = this.timezoneText;

        const [mp1] = state.locationDetails;
        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
            config.locationid = mp1.locationID;
        }

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    this.monitorSettingService.foreSiteUl(config, customer.customerID).subscribe(() => {
                        if (shouldClose) {
                            this.closeDialog(true);
                        }
                        this.showUpdateSuccessMessage(basicInfoPayload[0]['locationName']);
                    });
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService
                .addLocation(customer.customerID, basicInfoPayload)
                .subscribe({
                    next: (locationId: number) => {
                        config.locationid = locationId;
                        this.monitorSettingService.foreSiteUl(config, customer.customerID).subscribe(() => {
                            this.showSaveSuccessMessage(basicInfoPayload[0]['locationName']);
                            if (shouldClose) {
                                this.closeDialog(true);
                            } else {
                                this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.ForeSITE_UL);
                            }
                        });
                    },
                    error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
                });
        }
    }

    private submitForeSiteXDForm(customer: Customer, state: ForesiteXDLocationCardState, shouldClose: boolean) {
        const basicInfoPayload = gatherForeSiteXDBasicInfoPayload(state);
        if (!basicInfoPayload) {
            this.snackBar.open(
                this.translateService.instant('COMMON.INVALID_FORMAT'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
            );
            return;
        }

        const config: ForeSiteXDConfigurationPayload = gatherForeSiteXDconfigurationInfo(state);
        config.timezone = this.timezoneText;

        const [mp1] = state.locationDetails;
        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
            config.locationid = mp1.locationID;
        }

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    this.monitorSettingService.foreSITEXD(config, customer.customerID).subscribe(() => {
                        if (shouldClose) {
                            this.closeDialog(true);
                        }
                        this.showUpdateSuccessMessage(basicInfoPayload[0]['locationName']);
                    });
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService
                .addLocation(customer.customerID, basicInfoPayload)
                .subscribe({
                    next: (locationId: number) => {
                        this.showSaveSuccessMessage(basicInfoPayload[0]['locationName']);

                        config.locationid = locationId;
                        this.monitorSettingService.foreSITEXD(config, customer.customerID).subscribe(() => {
                            if (shouldClose) {
                                this.closeDialog(true);
                            } else {
                                this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.ForeSITE_XD);
                            }
                        });
                    },
                    error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
                });
        }
    }

    private submitFlexForm(customer: Customer, state: FlexLocationCardState, shouldClose: boolean) {
        const cancelTxt = this.translateService.instant('COMMON.CANCEL');
        const message = this.translateService.instant('COMMON.FLEX_LOCATION_CARD_SAVE_MESSAGE');
        const title = this.translateService.instant('ADD_EDIT.EDIT_CONFIRM_DIALOG');
        const continueTxt = this.translateService.instant('COMMON.CONTINUE_TEXT');

        const isEditMode = state.locationCardData.editMode;

        if (isEditMode) {
            this.dialog
                .open<ConfirmationDialogComponent, IComponentDialog, IComponentDialogConfirmationResult>(
                    ConfirmationDialogComponent,
                    {
                        data: {
                            title: title,
                            message: message,
                            cancelText: cancelTxt,
                            okText: continueTxt,
                        },
                    },
                )
                .afterClosed()
                .subscribe((res) => {
                    if (res.whichButtonWasPressed === ConfirmationButton.ok) {
                        this.saveFlexForm(customer, state, shouldClose);
                    } else {
                        this.closeDialog(true);
                    }
                });
        } else {
            this.linkFlexLocationWithIoTHub(customer, state, true);
        }
    }

    private linkFlexLocationWithIoTHub(customer: Customer, state: FlexLocationCardState, createFromPrism: boolean) {
        const serialNumber = findFieldById(
            state.formState,
            BasicInfoCommonFieldIds.SerialNumber,
            FieldType.Text,
        )?.value;

        this.store.dispatch(flexSetCreateMode({ mode: FlexCreateMode.wait }));

        this.tritonLocationService.linkFlexLocationWithIoTHub(customer.customerID, serialNumber, createFromPrism).subscribe({
            next: (locationId: number) => {
                this.store.dispatch(transitionToEditMode({ locationId, customerId: customer.customerID }));
            },
            error: (error: HttpErrorResponse) => {
                const message = typeof error?.error === 'string' ? error.error : (error?.error?.responseMessage ?? 'ADD_EDIT.FLEX_CREATE_FAIL');

                this.store.dispatch(flexSetCreateMode({ mode: FlexCreateMode.error, message }));
            },
        });
    }

    private saveFlexForm(customer: Customer, state: FlexLocationCardState, shouldClose: boolean) {
        const basicInfoPayload: FlexLocationPayload[] = gatherFlexBasicInfoPayload(state);
        const config = gatherFlexConfigurationInfo(state);

        if (!config) {
            return;
        }

        const [mp1] = state.locationDetails;
        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
        }

        if (mp1) {
            const locationId = basicInfoPayload[0].locationid;
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    const sensorCollection = gatherFlexDynamicSectionPayload(state);

                    this.monitorSettingService
                        .flexConfiguration(config, customer.customerID, locationId)
                        .subscribe(() => {
                            if (shouldClose) {
                                this.closeDialog(true);
                            }
                            this.showUpdateSuccessMessage(basicInfoPayload[0]['locationName']);
                        });

                    this.locationService
                        .saveSensorConfigurationCollection(basicInfoPayload[0].serialnumber, customer.customerID, sensorCollection)
                        .subscribe(() => {});
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService.addLocation(customer.customerID, basicInfoPayload).subscribe({
                next: (locationId: number) => {
                    config.locationid = locationId;
                    this.monitorSettingService
                        .flexConfiguration(config, customer.customerID, locationId)
                        .subscribe(() => {
                            if (shouldClose) {
                                this.closeDialog(true);
                            }
                            this.showSaveSuccessMessage(basicInfoPayload[0]['locationName']);
                        });

                    const sensorCollection = gatherFlexDynamicSectionPayload(state);
                    this.locationService
                        .saveSensorConfigurationCollection(basicInfoPayload[0].serialnumber, customer.customerID, sensorCollection)
                        .subscribe();
                },
                error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
            });
        }
    }

    private submitGenericForm(customer: Customer, state: GenericLocationCardState, shouldClose: boolean) {
        const basicInfoPayload: GenericLocationPayload[] = gatherGenericBasicInfoPayload(state);
        const [mp1] = state.locationDetails;

        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
        }

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    if (shouldClose) {
                        this.closeDialog(true);
                    }
                    this.showUpdateSuccessMessage(basicInfoPayload[0]['locationName']);
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService.addLocation(customer.customerID, basicInfoPayload).subscribe({
                next: (locationId: number) => {
                    this.showSaveSuccessMessage(basicInfoPayload[0]['locationName']);
                    if (shouldClose) {
                        this.closeDialog(true);
                    } else {
                        this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.genericMonitor);
                    }
                },
                error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
            });
        }
    }

    private submitEdgeDxForm(customer: Customer, state: LocationCardState<unknown>, shouldClose: boolean): void {
        if (!state.valid) {
            return;
        }

        let payload: EdgeDxLocationPayload[];
        try {
            payload = gatherEdgeDxBasicInfoPayload(state);
        } catch {
            this.snackBar.open(
                this.translateService.instant('COMMON.INVALID_FORMAT'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
            );
            return;
        }

        const [location] = payload;
        if (!location) {
            return;
        }
        const locationId = state.locationDetails[0]?.locationID;
        if (locationId) {
            location.locationid = locationId;
            this.updateLocationWithRetry(customer.customerID, payload).subscribe({
                next: () => {
                    this.showUpdateSuccessMessage(location.locationName);
                    if (shouldClose) {
                        this.closeDialog(true);
                    } else {
                        this.reloadEdgeDxLocation(customer.customerID, locationId, state);
                    }
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService.addLocation(customer.customerID, payload).subscribe({
                next: (locationId: number) => {
                    this.showSaveSuccessMessage(location.locationName);
                    if (shouldClose) {
                        this.closeDialog(true);
                    } else {
                        this.reloadEdgeDxLocation(customer.customerID, locationId, state);
                    }
                },
                error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
            });
        }
    }

    private reloadEdgeDxLocation(customerId: number, locationId: number, state: LocationCardState<unknown>): void {
        const handleError = (): void => {
            this.snackBar.open(
                this.translateService.instant('COMMON.LOCATION_FETCHED_ERROR'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
            );
            if (state.locationDetails.length === 0) {
                this.closeDialog(true);
            }
        };
        this.locationService.getLocationDetailsV2(customerId, locationId).subscribe({
            next: (details) => {
                const [locationDetails] = details;
                if (!locationDetails) {
                    handleError();
                    return;
                }
                this.store.dispatch(loadLocationDetailsSuccess({ details }));
                this.store.dispatch(setFormData({
                    data: { ...state.locationCardData, editMode: true, locationDetails },
                }));
                this.store.dispatch(populateForm({ details, config: null, series: state.options.monitorSeries.data }));
                this.store.dispatch(transitionToEditModeSuccess());
            },
            error: handleError,
        });
    }

    private submitEdgeRlForm(customer: Customer, state: EdgeRlLocationCardState, shouldClose: boolean) {
        const basicInfoPayload: EdgeRlLocationPayload[] = gatherEdgeRlBasicInfoPayload(state);
        const edgeRlConfiguration: EdgeRlConfigurationPayload = gatherEdgeRlConfigurationInfo(state);
        edgeRlConfiguration.timezone = this.timezoneText;

        const [mp1] = state.locationDetails;

        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
            edgeRlConfiguration.locationid = mp1.locationID;
        }

        const locationName: string = basicInfoPayload[0]['locationName'];

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    this.monitorSettingService
                        .edgeRlConfiguration(edgeRlConfiguration, customer.customerID)
                        .subscribe(() => {
                            this.showUpdateSuccessMessage(locationName);
                            if (shouldClose) {
                                this.closeDialog(true);
                            }
                        });
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService.addLocation(customer.customerID, basicInfoPayload).subscribe({
                next: (locationId: number) => {
                    edgeRlConfiguration.locationid = locationId;
                    this.monitorSettingService
                        .edgeRlConfiguration(edgeRlConfiguration, customer.customerID)
                        .subscribe(() => {
                            this.showSaveSuccessMessage(locationName);
                            if (shouldClose) {
                                this.closeDialog(true);
                            } else {
                                this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.edgeRl);
                            }
                        });
                },
                error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
            });
        }
    }

    private submitOtherMonitorForm(customer: Customer, state: LocationCardState<unknown>, shouldClose: boolean) {
        const basicInfoPayload: UnknownLocationPayload[] = gatherUnknownBasicInfoPayload(state as UnknownLocationCardState);
        const [mp1] = state.locationDetails;

        if (mp1) {
            basicInfoPayload[0].locationid = mp1.locationID;
        }

        if (mp1) {
            this.updateLocationWithRetry(customer.customerID, basicInfoPayload).subscribe({
                next: () => {
                    if (shouldClose) {
                        this.closeDialog(true);
                    }
                    this.showUpdateSuccessMessage(basicInfoPayload[0]['locationName']);
                },
                error: (error: HttpErrorResponse) => this.handleUpdateLocationError(error),
            });
        } else {
            this.tritonLocationService.addLocation(customer.customerID, basicInfoPayload).subscribe({
                next: (locationId: number) => {
                    this.showSaveSuccessMessage(basicInfoPayload[0]['locationName']);
                    if (shouldClose) {
                        this.closeDialog(true);
                    } else {
                        this.handlePostAddTransition(customer.customerID, locationId, state, MonitorSeriesNames.usgs);
                    }
                },
                error: (error: HttpErrorResponse) => this.handleCreateLocationError(error),
            });
        }
    }

    // ACTION HANDLERS

    // #49696 Ignore this parameter for now.
    private closeDialog(result?: boolean) {
        this.mapService.closeDialog(true);
    }

    private showSaveSuccessMessage(locationName: string) {
        this.snackBar.open(
            locationName + this.translateService.instant('COMMON.LOCATION_ADDED_MESSAGE'),
            this.translateService.instant('COMMON.DISMISS_TEXT'),
            {
                duration: 3000,
            },
        );
    }

    private showUpdateSuccessMessage(locationName: string) {
        this.snackBar.open(
            `${locationName} ${this.translateService.instant('COMMON.UPDATED_SUCCESSFULLY')}`,
            this.translateService.instant('COMMON.DISMISS_TEXT'),
            {
                duration: 10000,
            },
        );
    }

    private handlePostAddTransition(customerId: number, locationId: number, state: LocationCardState<unknown>, series: string) {
        this.locationService.getLocationDetailsV2(customerId, locationId).subscribe((details: LocationDetails[]) => {
            this.store.dispatch(loadLocationDetailsSuccess({ details }));
            this.store.dispatch(setFormData({
                data: {
                    ...state.locationCardData,
                    editMode: true,
                    locationDetails: details[0],
                },
            }));
            this.store.dispatch(transitionToEditModeSuccess());
            this.loadMonitorConfiguration(customerId, locationId, series);
        });
    }

    private loadMonitorConfiguration(customerId: number, locationId: number, series: string): void {
        const configRequest = this.getMonitorConfigurationRequest(customerId, locationId, series);
        if (configRequest) {
            configRequest.subscribe((config) => {
                this.store.dispatch(loadMonitorConfigurationSuccess({ config }));
            });
        }
    }

    private getMonitorConfigurationRequest(customerId: number, locationId: number, series: string): Observable<MonitorConfiguration> | null {
        switch (series.toLowerCase()) {
            case MonitorSeriesNames.triton.toLowerCase():
                return this.tritonLocationService.getTritonMonitorConfiguration(locationId, customerId);
            case MonitorSeriesNames.echo.toLowerCase():
                return this.monitorSettingService.getEchoMonitorConfiguration(customerId, locationId);
            case MonitorSeriesNames.rainAlert.toLowerCase():
                return this.monitorSettingService.getRainAlertMonitorConfiguration(customerId, locationId);
            case MonitorSeriesNames.ForeSITE_UL.toLowerCase():
                return this.monitorSettingService.foreSiteUlConfiguration(customerId, locationId);
            case MonitorSeriesNames.ForeSITE_XD.toLowerCase():
                return this.monitorSettingService.getForeSITEXDConfiguration(customerId, locationId);
            default:
                return null;
        }
    }

    private handlePostConfigActivation(
        isActivationRequired: boolean,
        isInactive: boolean,
        customerId: number,
        locationId: number,
        locationName: string,
        shouldClose: boolean,
    ) {
        if (isActivationRequired && !isInactive) {
            this.activateMonitor(customerId, locationId, locationName, shouldClose);
        } else if (shouldClose) {
            this.closeDialog(true);
        }
    }

    private activateMonitor(customerId: number, locationId: number, locationName: string, shouldClose: boolean) {
        this.tritonLocationService.activateLocation(customerId, locationId).subscribe({
            next: (result: ActivateTritonResponse) => {
                this.snackBar.open(result.responseMessage, this.translateService.instant('COMMON.DISMISS_TEXT'), {
                    duration: 10000,
                });
                if (shouldClose) {
                    this.closeDialog(true);
                }
            },
            error: (error: HttpErrorResponse) => {
                const responseMessage = error?.error?.responseMessage;
                if (responseMessage) {
                    this.snackBar.open(responseMessage, this.translateService.instant('COMMON.DISMISS_TEXT'), {
                        panelClass: 'custom-error-snack-bar',
                    });
                } else {
                    this.snackBar.open(
                        `${locationName} ${this.translateService.instant('ADD_EDIT.ACTIVATION_HAS_FAILED')}`,
                        this.translateService.instant('COMMON.DISMISS_TEXT'),
                        { panelClass: 'custom-error-snack-bar' },
                    );
                }
            },
        });
    }

    private updateLocationWithRetry(customerId: number, payload: LocationPayload[]) {
        return this.tritonLocationService.updateLocation(customerId, payload).pipe(
            retry({
                count: 1,
                delay: (error: HttpErrorResponse) =>
                    error.status === HTTP_STATUS_CODE_CONCURRENCY
                        ? of(true)
                        : throwError(() => error),
            }),
        );
    }

    private handleCreateLocationError(error: HttpErrorResponse) {
        if (error.status === HTTP_STATUS_CODE_CONFLICT) {
            this.snackBar.open(error.error, this.translateService.instant('COMMON.DISMISS_TEXT'), {
                panelClass: 'custom-error-snack-bar',
            });
        } else {
            this.snackBar.open(
                this.translateService.instant('COMMON.LOCATION_ADDED_ERROR'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
                {
                    panelClass: 'custom-error-snack-bar',
                },
            );
        }
    }

    private handleUpdateLocationError(error: HttpErrorResponse) {
        if (error.status === HTTP_STATUS_CODE_CONFLICT) {
            this.snackBar.open(error.error, this.translateService.instant('COMMON.DISMISS_TEXT'), {
                panelClass: 'custom-error-snack-bar',
            });
        } else {
            this.snackBar.open(
                this.translateService.instant('COMMON.LOCATION_UPDATED_ERROR'),
                this.translateService.instant('COMMON.CLOSE'),
                {
                    panelClass: 'custom-error-snack-bar',
                },
            );
        }
    }

    private extractLatestSiltValue(entityData: GainEntityData): number {
        const map = new Map();
        entityData.data.forEach((item) => map.set(item.dateTime, item));
        entityData.editedData.forEach((item) => map.set(item.dateTime, { ...map.get(item.dateTime), ...item }));

        const mergedData = Array.from(map.values()).filter((d) => !d['ignore']);

        if (mergedData.length > 0) {
            const sortedData = mergedData.sort(
                (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
            );

            return Number(sortedData[0].reading) || 0;
        }

        return 0;
    }

    private loadTimezone(customer: Customer) {
        this.customerService.getTimeZone().subscribe((timezones) => {
            const tz = timezones.find((v) => v.value === customer.timeZone);
            this.timezoneText = tz?.text ?? '';
        });
    }
}
